import React, { useState } from 'react';

import { CloseOutlined } from '@ant-design/icons';
import { Form, Input, Tooltip } from 'antd';
import styled from 'styled-components';

import Button from 'components/lib/Button';
import ButtonPill from 'components/lib/ButtonPill';
import Checkbox from 'components/lib/Checkbox';
import InputNumber from 'components/lib/InputNumber';
import { COMMUNITY_LIFE_PLAN_CONTRACT_TYPES } from 'constants/contracts';
import {
  COMMUNITY_CCRC_CONTRACT_TYPES,
  COMMUNITY_CCRC_CONTRACT_TYPES_LABELS,
  COMMUNITY_RENTAL_TYPE,
  COMMUNITY_RENTAL_TYPE_LABELS,
} from 'constants/rentalTypes';

import { COMMUNITY_CONTRACT_TYPE, CONTRACT_SLUG_PREFIX } from '../../../../constants';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  padding-left: 30px;
  font-size: 12px;
  gap: 20px;
`;
const Info = styled.span`
  display: flex;
  color: var(--text-secondary);
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
`;

const Selection = styled.div`
  display: flex;
  gap: 10px;
`;

interface Props {
  error?: string;
  disableRental?: boolean;
  disableCCRC?: boolean;
}

const buildRentalTypeOption = (value: number, disabled: boolean | undefined, disabledTooltip: string) => {
  const label = COMMUNITY_RENTAL_TYPE_LABELS[value];
  return {
    value,
    label: disabled ? <Tooltip title={disabledTooltip}>{label}</Tooltip> : label,
    disabled,
  };
};

const BasicInfoEditFormRentalType = ({ error, disableRental, disableCCRC }: Props) => {
  const [selectedContractType, setSelectedContractType] = useState<number>(COMMUNITY_CONTRACT_TYPE.TYPE_A);
  const rentalTypeOptions = [
    buildRentalTypeOption(
      COMMUNITY_RENTAL_TYPE.RENTAL,
      disableRental,
      'Rental cannot be removed while rental data exists'
    ),
    buildRentalTypeOption(
      COMMUNITY_RENTAL_TYPE.CCRC,
      disableCCRC,
      'Life Plan cannot be removed while life plan data exists'
    ),
  ];

  return (
    <>
      <Form.Item
        name="rental_types"
        label="Community Type"
        rules={[{ required: true, message: 'Please select at least one community rental type' }]}
      >
        <Checkbox.Group className={'green'} options={rentalTypeOptions} />
      </Form.Item>
      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue }) => {
          const rental_types: number[] = getFieldValue('rental_types') || [];

          const showLifePlanOptions = rental_types.includes(COMMUNITY_RENTAL_TYPE.CCRC);

          if (!showLifePlanOptions) {
            return null;
          }

          return (
            <Container>
              <Info>
                Set up refund specs for a life plan community type below. They will appear in community data entry with
                accommodation prices.
              </Info>
              <Selection>
                {COMMUNITY_CCRC_CONTRACT_TYPES.map((item: number) => (
                  <ButtonPill
                    type={selectedContractType === item ? 'primary' : 'default'}
                    style={{ height: 26, fontSize: 12, border: 'none' }}
                    key={item}
                    onClick={() => setSelectedContractType(item)}
                  >
                    {COMMUNITY_CCRC_CONTRACT_TYPES_LABELS[item]}
                  </ButtonPill>
                ))}
              </Selection>
              <Content>
                {COMMUNITY_LIFE_PLAN_CONTRACT_TYPES.map((type) => (
                  <div key={type} style={{ display: type === selectedContractType ? 'block' : 'none' }}>
                    <Form.List name={['contracts', `${CONTRACT_SLUG_PREFIX}-${type}`]}>
                      {(fields, { add, remove }) => (
                        <div>
                          <div>Percentage-based refund</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <div style={{ display: 'flex', gap: 10, maxWidth: '80%', flexWrap: 'wrap' }}>
                              {fields.map(({ name }, index) => (
                                <div style={{ display: 'flex' }} key={name}>
                                  <Form.Item
                                    name={[name, 'refund_percentage']}
                                    rules={[{ required: true, message: 'Enter refund %' }]}
                                    noStyle
                                  >
                                    <InputNumber
                                      name={name}
                                      style={{ width: 80, height: 36 }}
                                      max={100}
                                      min={0}
                                      suffix={'%'}
                                    />
                                  </Form.Item>
                                  <Button
                                    style={{ color: 'var(--text-secondary)', width: 20, height: 36, fontSize: 10 }}
                                    type={'link'}
                                    icon={<CloseOutlined />}
                                    onClick={() => remove(name)}
                                  />
                                </div>
                              ))}
                            </div>
                            <Button
                              style={{ color: 'var(--dark-blue)' }}
                              onClick={() =>
                                add({
                                  id: undefined, // or omit if it's for new entries
                                  type: selectedContractType,
                                  refund_percentage: undefined,
                                })
                              }
                              type={'link'}
                            >
                              + Add
                            </Button>
                          </div>
                        </div>
                      )}
                    </Form.List>
                  </div>
                ))}
                {error && <div style={{ color: 'var(--negative-orange)' }}>{error}</div>}
              </Content>
            </Container>
          );
        }}
      </Form.Item>
    </>
  );
};

export default BasicInfoEditFormRentalType;
